import { useEffect, useState } from "react";
import { Building2, Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from "@/components/ui/select";
import { programmesApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";

/**
 * Reusable dropdown of declared real-estate programmes.
 *
 * Props:
 * - value: current programme name (string) — case-sensitive match against `nom`
 * - onChange: (newName: string) => void
 * - placeholder: text when no programme selected
 * - showInactive: if true, includes "complet/livré/archivé" programmes too
 * - allowEmpty: if true, adds a "— Aucun —" option that maps to ""
 * - testId
 */
export default function ProgrammeSelect({
  value,
  onChange,
  placeholder = "Choisir un programme",
  showInactive = false,
  allowEmpty = true,
  testId = "programme-select",
  className,
}) {
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    programmesApi
      .list()
      .then((list) => {
        if (cancelled) return;
        setProgrammes(list || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setProgrammes([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // The Select value must match exactly an item or be empty.
  // If `value` doesn't match any programme (e.g., legacy free-text), we still
  // show it as a "ghost" option so the user doesn't lose data on save.
  const matched = programmes.find((p) => p.nom === value);
  const ghostValue = value && !matched ? value : null;

  // Group programmes: actives en haut, autres en dessous
  const actives = programmes.filter((p) => p.statut === "actif");
  const others = programmes.filter((p) => p.statut !== "actif");

  // Internal sentinel because Radix Select disallows value=""
  const SENTINEL_EMPTY = "__none__";
  const internalValue = !value ? SENTINEL_EMPTY : value;
  const handleChange = (v) => {
    onChange?.(v === SENTINEL_EMPTY ? "" : v);
  };

  return (
    <Select value={internalValue} onValueChange={handleChange}>
      <SelectTrigger data-testid={testId} className={cn("text-sm", className)}>
        <Building2 className="mr-1.5 h-3.5 w-3.5 text-accent-gold" />
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowEmpty && (
          <SelectItem value={SENTINEL_EMPTY} className="text-muted-foreground italic">
            — Aucun (laisser vide) —
          </SelectItem>
        )}
        {ghostValue && (
          <>
            {allowEmpty && <SelectSeparator />}
            <SelectGroup>
              <SelectLabel className="text-amber-600">Programme libre (non répertorié)</SelectLabel>
              <SelectItem value={ghostValue} className="font-medium">
                ⚠️ {ghostValue}
              </SelectItem>
            </SelectGroup>
          </>
        )}
        {actives.length > 0 && (
          <>
            {(allowEmpty || ghostValue) && <SelectSeparator />}
            <SelectGroup>
              <SelectLabel>En commercialisation</SelectLabel>
              {actives.map((p) => (
                <SelectItem key={p.id} value={p.nom}>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{p.nom}</span>
                    {(p.localisation || p.typologie) && (
                      <span className="text-[10px] text-muted-foreground">
                        {[p.localisation, p.typologie].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </>
        )}
        {others.length > 0 && showInactive && (
          <>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>Autres (complet · livré · archivé)</SelectLabel>
              {others.map((p) => (
                <SelectItem key={p.id} value={p.nom}>
                  {p.nom}
                  <span className="ml-1 text-[10px] text-muted-foreground">({p.statut})</span>
                </SelectItem>
              ))}
            </SelectGroup>
          </>
        )}
        {programmes.length === 0 && !loading && (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            Aucun programme déclaré.
            <br />
            Ajoutez-en un dans <strong>Paramètres → Catalogue programmes</strong>.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
